import { useMemo, useState, type ReactNode } from "react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ImagePlus,
  Loader2,
  MessageSquareText,
  Package2,
  Sparkles,
  Store,
  PauseCircle,
  Play,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useConversionsAll } from "@/lib/hooks/useConversions";
import { useGroups } from "@/lib/hooks/useGroups";
import {
  useTelegramCopyRoutes,
  useUpdateTelegramCopyRouteStatus,
} from "@/lib/hooks/useTelegramCopyRoutes";
import { useCredentials } from "@/lib/hooks/useCredentials";
import { useLinks } from "@/lib/hooks/useLinks";
import type { ConversionAttempt, Link, TelegramGroup, TelegramCopyRoute } from "@/lib/types";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/PageHeader";
import { GenerateLinkPanel } from "@/components/links/GenerateLinkPanel";
import { CopyGeneratorDrawer } from "@/components/CopyGeneratorDrawer";
import { ImageGeneratorDrawer } from "@/components/ai-images/ImageGeneratorDrawer";

type NoticeTone = "neutral" | "success" | "warning" | "danger";

interface MessageAttemptGroup {
  key: string;
  sourceLabel: string;
  destinationLabels: readonly string[];
  attempts: ConversionAttempt[];
  latestCreatedAt: string;
}

export function LinksPage(): JSX.Element {
  const { data: credentials = [] } = useCredentials();
  const { data: links = [], isLoading, error: linksError } = useLinks();
  const {
    data: conversionAttempts = [],
    isLoading: loadingAttempts,
    error: attemptsError,
  } = useConversionsAll();
  const { data: groups = [], error: groupsError } = useGroups();
  const { data: copyRoutes = [], isLoading: loadingCopyRoutes, error: routesError } = useTelegramCopyRoutes();
  const {
    mutateAsync: updateRouteStatus,
    isPending: isUpdatingRoute,
  } = useUpdateTelegramCopyRouteStatus();

  const [activeRouteAction, setActiveRouteAction] = useState<string | null>(null);
  const [routeActionMessage, setRouteActionMessage] = useState<{
    tone: NoticeTone;
    message: string;
  } | null>(null);
  const [copyDrawerLink, setCopyDrawerLink] = useState<Link | null>(null);
  const [imageDrawerLink, setImageDrawerLink] = useState<Link | null>(null);

  const connectionAlerts = useMemo(
    () => credentials.filter((credential) => !isCredentialHealthy(credential.status)),
    [credentials],
  );
  const linksCount = links.length;

  const messageHistory = useMemo(
    () =>
      groupConversionsByMessage({
        conversions: conversionAttempts,
        groups,
        copyRoutes,
      }),
    [conversionAttempts, groups, copyRoutes],
  );

  const historyError = linksError ?? attemptsError ?? groupsError ?? routesError;
  const groupLookup = useMemo(
    () => new Map(groups.map((group) => [group.id, group])),
    [groups],
  );
  const activeRouteCount = copyRoutes.filter((route) => route.status === "active").length;
  const totalRouteCount = copyRoutes.length;

  const connectionStatusLabel = useMemo(() => {
    if (credentials.length === 0) {
      return "Sem credenciais carregadas";
    }
    if (connectionAlerts.length === 0) {
      return "Todas as conexões monitoradas em estado saudável";
    }
    if (connectionAlerts.length === 1) {
      return "1 conexão fora do estado esperado";
    }
    return `${connectionAlerts.length} conexões fora do estado esperado`;
  }, [connectionAlerts.length, credentials.length]);

  const handleToggleRoute = async (route: TelegramCopyRoute): Promise<void> => {
    const nextStatus = route.status === "active" ? "paused" : "active";
    setActiveRouteAction(route.id);
    setRouteActionMessage(null);

    try {
      await updateRouteStatus({
        id: route.id,
        status: nextStatus,
      });
      setRouteActionMessage({
        tone: "success",
        message:
          nextStatus === "active"
            ? "Rota de grupos reativada."
            : "Rota de grupos pausada para inspeção.",
      });
    } catch {
      setRouteActionMessage({
        tone: "danger",
        message: "Não foi possível atualizar o estado da rota agora.",
      });
    } finally {
      setActiveRouteAction(null);
    }
  };

  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader
        title="Links"
        subtitle="Gere links oficiais de Amazon, Mercado Livre, Shopee, Magalu, AliExpress, SHEIN e lojas Awin e Rakuten."
        actions={
          !isLoading && !linksError ? (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {linksCount} {linksCount === 1 ? "link" : "links"}
            </div>
          ) : undefined
        }
      />

      {historyError && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={16} className="shrink-0" />
          <span>Erro ao carregar histórico: {historyError.message}</span>
        </div>
      )}

      <GenerateLinkPanel />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.4fr)]">
        <Card variant="raised" className="overflow-hidden">
          <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Store size={18} />
              </div>
              <div className="space-y-1">
                <CardTitle>Central de integrações</CardTitle>
                <CardDescription>
                  Saúde das conexões e rotas de grupos do fluxo automático.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-5 pt-5">
            <InlineNotice tone={connectionAlerts.length > 0 ? "warning" : "neutral"}>{connectionStatusLabel}</InlineNotice>
            {routeActionMessage && <InlineNotice tone={routeActionMessage.tone}>{routeActionMessage.message}</InlineNotice>}

            <div className="rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Monitoramento de rotas</p>
                  <p className="mt-1 text-sm text-foreground">
                    {activeRouteCount} ativas de {totalRouteCount} no monitoramento de origem e destino.
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Origem e destino representam grupos que participam do repasse automático de mensagens.
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full border border-border bg-background/55 px-2.5 py-1 text-xs text-muted-foreground">
                  {loadingCopyRoutes ? "checando regras..." : `${totalRouteCount} regra(s)`}
                </span>
              </div>
              <div className="mt-3 space-y-2">
                {loadingCopyRoutes && (
                  <div className="h-10 animate-pulse rounded border border-border bg-background/60" />
                )}
                {!loadingCopyRoutes && copyRoutes.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhuma rota definida para grupos ainda. Abra a página Grupos para criar origem e destino.
                  </p>
                )}
                {!loadingCopyRoutes && copyRoutes.length > 0 && (
                  <div className="divide-y divide-border rounded-md border border-border">
                    {copyRoutes.slice(0, 6).map((route) => (
                      <div key={route.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <MetaBadge
                            icon={<Users size={11} />}
                            label={`origem: ${groupTitle(groupLookup.get(route.source_group_id), route.source_group_id)}`}
                          />
                          <ArrowRight size={12} className="text-muted-foreground/80" />
                          <MetaBadge
                            label={`destino: ${groupTitle(groupLookup.get(route.destination_group_id), route.destination_group_id)}`}
                          />
                          <MetaBadge label={groupRouteStatusLabel(route.status)} />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleRoute(route)}
                          disabled={activeRouteAction === route.id || isUpdatingRoute}
                        >
                          {activeRouteAction === route.id && isUpdatingRoute ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : route.status === "active" ? (
                            <PauseCircle size={13} />
                          ) : (
                            <Play size={13} />
                          )}
                          <span>{route.status === "active" ? "Pausar" : "Retomar"}</span>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--gold)/0.10)] text-[hsl(var(--gold))]">
                <Package2 size={18} />
              </div>
              <div className="space-y-1">
                <CardTitle>Histórico por mensagem</CardTitle>
                <CardDescription>
                  Origem, destino e status de entrega para mensagens com links processados no tenant.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {loadingAttempts && !historyError && (
              <div className="space-y-2 p-5" aria-busy="true" aria-label="Carregando histórico">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-24 w-full animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.35)]"
                  />
                ))}
              </div>
            )}

            {!loadingAttempts && !historyError && messageHistory.length === 0 && linksCount === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MessageSquareText size={24} />
                </div>
                <div className="max-w-md space-y-2">
                  <p className="font-semibold text-foreground">Ainda sem atividade</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Quando houver conversão no fluxo do bot, o histórico por mensagem aparece aqui com origem e destino.
                  </p>
                </div>
                <a
                  href="/app/grupos"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-[hsl(var(--surface-raised))] px-4 py-2 text-xs font-medium text-foreground hover:bg-[hsl(var(--surface-raised)/0.7)] transition-colors"
                >
                  <Users size={14} />
                  Ir para grupos
                  <ArrowRight size={12} />
                </a>
              </div>
            )}

            {!loadingAttempts && !historyError && messageHistory.length > 0 && (
              <div className="divide-y divide-border">
                {messageHistory.map((group) => (
                  <MessageHistoryItem key={group.key} group={group} />
                ))}
              </div>
            )}

            {!loadingAttempts && !historyError && linksCount > 0 && (
              <div className="border-t border-border bg-[hsl(var(--surface-raised)/0.25)] px-5 py-4">
                <div className="space-y-1 mb-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Links salvos</p>
                  <p className="text-xs text-muted-foreground">Fallback manual por marketplace.</p>
                </div>
                <div className="divide-y divide-border">
                  {links.map((link) => (
                    <LinkHistoryItem
                      key={link.id}
                      link={link}
                      onGenerateCopy={setCopyDrawerLink}
                      onGenerateImage={setImageDrawerLink}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CopyGeneratorDrawer
        open={copyDrawerLink !== null}
        onClose={() => setCopyDrawerLink(null)}
        productUrl={copyDrawerLink?.original_url ?? ""}
        affiliateUrl={copyDrawerLink?.destination_url ?? ""}
        marketplaceLabel={copyDrawerLink ? marketplaceLabel(copyDrawerLink.marketplace_code) : ""}
      />

      <ImageGeneratorDrawer
        open={imageDrawerLink !== null}
        onClose={() => setImageDrawerLink(null)}
        productUrl={imageDrawerLink?.original_url ?? ""}
      />
    </div>
  );
}

function MessageHistoryItem({ group }: { group: MessageAttemptGroup }): JSX.Element {
  const deliveryOverview = statusSummary(group.attempts);
  const messageUrl = group.attempts[0]?.original_url ?? "-";
  return (
    <article className="space-y-3 px-5 py-4 transition-colors hover:bg-[hsl(var(--surface-raised)/0.28)]">
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{shortenUrl(messageUrl)}</p>
        <p className="text-sm text-foreground">{messageUrl}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <MetaBadge label={`origem: ${group.sourceLabel}`} />
        <MetaBadge label={`destino: ${group.destinationLabels.join(", ")}`} />
        <MetaBadge label={`tentativas: ${group.attempts.length}`} />
        <MetaBadge label={`entrega: ${deliveryOverview}`} />
        <MetaBadge label={`última: ${timeAgo(group.latestCreatedAt)}`} />
      </div>

      <div className="space-y-2">
        {group.attempts.map((attempt) => {
          const attemptStatus = deliveryStatusLabel(attempt.status);
          return (
            <div
              key={attempt.id}
              className="rounded-md border border-border bg-background/55 px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                {attempt.marketplace_code ? <MarketplaceBadge code={attempt.marketplace_code} /> : null}
                <StatusBadge status={attempt.status} />
                <MetaBadge label={`status: ${attemptStatus}`} />
                {attempt.telegram_message_id ? (
                  <MetaBadge label={`msg ${attempt.telegram_message_id}`} />
                ) : null}
              </div>
              {attempt.error_code && <p className="mt-1 text-xs text-destructive">{attempt.error_code}</p>}
            </div>
          );
        })}
      </div>
    </article>
  );
}

function LinkHistoryItem({
  link,
  onGenerateCopy,
  onGenerateImage,
}: {
  link: Link;
  onGenerateCopy: (link: Link) => void;
  onGenerateImage: (link: Link) => void;
}): JSX.Element {
  return (
    <article className="space-y-4 px-5 py-4 transition-colors hover:bg-[hsl(var(--surface-raised)/0.28)]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <MarketplaceBadge code={link.marketplace_code} />
            <MetaBadge label={modeLabel(link.mode)} />
            <MetaBadge label={statusLabel(link.status)} />
            {link.created_from && <MetaBadge label={`origem: ${sourceLabel(link.created_from)}`} />}
          </div>

          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">URL original</p>
            <p className="font-mono text-xs text-foreground break-all">{link.original_url}</p>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Destino</p>
            <p className="font-mono text-xs text-muted-foreground break-all">{link.destination_url}</p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end">
          <div className="space-y-1 text-left lg:text-right">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Criado em</p>
            <p className="text-sm text-foreground">{formatDateTime(link.created_at)}</p>
            <p className="font-mono text-[11px] text-muted-foreground">{link.id}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => onGenerateCopy(link)}>
              <Sparkles size={13} />
              Gerar copy com IA
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onGenerateImage(link)}>
              <ImagePlus size={13} />
              Gerar imagem
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

function InlineNotice({
  children,
  tone,
}: {
  children: ReactNode;
  tone: NoticeTone;
}): JSX.Element {
  const toneClasses: Record<NoticeTone, string> = {
    neutral: "border-border bg-[hsl(var(--surface)/0.75)] text-muted-foreground",
    success: "border-primary/25 bg-primary/10 text-primary",
    warning: "border-[hsl(var(--gold)/0.25)] bg-[hsl(var(--gold)/0.10)] text-[hsl(var(--gold))]",
    danger: "border-destructive/30 bg-destructive/10 text-destructive",
  };

  return (
    <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3 text-sm", toneClasses[tone])}>
      {tone === "success" ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

function MarketplaceBadge({ code }: { code: string }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-md border border-border bg-[hsl(var(--surface-raised))] px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {marketplaceLabel(code)}
    </span>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const label = deliveryStatusLabel(status);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
        status === "success" || status === "delivered"
          ? "bg-primary/10 text-primary border-primary/25"
          : status === "skipped"
            ? "bg-muted text-muted-foreground border-transparent"
            : "bg-destructive/10 text-destructive border-destructive/25",
      )}
    >
      {label}
    </span>
  );
}

function MetaBadge({ icon, label }: { icon?: ReactNode; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border/80 bg-background/40 px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {icon}
      {label}
    </span>
  );
}

function marketplaceLabel(code: string): string {
  const labels: Record<string, string> = {
    amazon: "Amazon",
    mercadolivre: "Mercado Livre",
    shopee: "Shopee",
    aliexpress: "AliExpress",
    magalu: "Magalu",
    awin: "AWIN",
    shein: "SHEIN",
  };

  return labels[code] ?? code;
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: "Ativo",
    valid: "Válida",
    invalid: "Inválida",
    disabled: "Desativada",
    pending: "Pendente",
    needs_approval: "Aguardando aprovação",
  };

  return labels[status] ?? status;
}

function groupRouteStatusLabel(status: string): string {
  return status === "active" ? "Ativa" : "Pausada";
}

function statusSummary(attempts: readonly ConversionAttempt[]): string {
  if (attempts.length === 0) {
    return "sem registro";
  }

  const hasError = attempts.some((attempt) => attempt.status === "error");
  if (hasError) return "erro";

  const allDelivered = attempts.every((attempt) => attempt.status === "success" || attempt.status === "delivered");
  if (allDelivered) return "entregue";

  const pending = attempts.every((attempt) => attempt.status === "pending");
  if (pending) return "pendente";

  return "parcial";
}

function deliveryStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    success: "Entrega ok",
    error: "Falhou",
    skipped: "Ignorado",
    pending: "Pendente",
    delivered: "Entregue",
  };

  return labels[status] ?? status;
}

function modeLabel(mode: string): string {
  return mode === "deeplink" ? "direto" : mode === "shortener" ? "shortener" : mode;
}

function sourceLabel(source: string): string {
  const labels: Record<string, string> = {
    manual: "manual",
    manual_official: "oficial importado",
    bot: "bot",
  };

  return labels[source] ?? source;
}

function groupConversionsByMessage(params: {
  conversions: readonly ConversionAttempt[];
  groups: readonly TelegramGroup[];
  copyRoutes: readonly TelegramCopyRoute[];
}): MessageAttemptGroup[] {
  const { conversions, groups, copyRoutes } = params;

  const groupsById = new Map<string, TelegramGroup>(groups.map((group) => [group.id, group]));
  const routesBySource = new Map<string, TelegramCopyRoute[]>();

  for (const route of copyRoutes) {
    const current = routesBySource.get(route.source_group_id) ?? [];
    routesBySource.set(route.source_group_id, [...current, route]);
  }

  const groupsByMessage = new Map<string, MessageAttemptGroup>();

  for (const attempt of conversions) {
    const messageKey = makeMessageKey(attempt);
    const existing = groupsByMessage.get(messageKey);
    if (!existing) {
      const sourceLabel = resolveSourceLabel(attempt.group_id, groupsById.get(attempt.group_id ?? ""));
      const routeInfo = resolveRouteInfo(attempt.group_id, routesBySource, groupsById);

      groupsByMessage.set(messageKey, {
        key: messageKey,
        sourceLabel,
        destinationLabels: routeInfo.destinationLabels,
        attempts: [attempt],
        latestCreatedAt: attempt.created_at,
      });
      continue;
    }

    const nextAttempts = [...existing.attempts, attempt].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    const nextLatest = new Date(attempt.created_at).getTime() > new Date(existing.latestCreatedAt).getTime()
      ? attempt.created_at
      : existing.latestCreatedAt;

    groupsByMessage.set(messageKey, {
      ...existing,
      attempts: nextAttempts,
      latestCreatedAt: nextLatest,
    });
  }

  return [...groupsByMessage.values()].sort(
    (a, b) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime(),
  );
}

function makeMessageKey(attempt: ConversionAttempt): string {
  if (attempt.telegram_message_id !== null) {
    return `msg-${attempt.telegram_message_id}`;
  }

  if (attempt.link_id) {
    return `link-${attempt.link_id}`;
  }

  return `fallback-${attempt.id}`;
}

function resolveSourceLabel(
  groupId: string | null,
  group: TelegramGroup | undefined,
): string {
  if (!groupId || !group) return "origem não rastreada";
  return group.title ?? String(group.telegram_chat_id);
}

function resolveRouteInfo(
  sourceGroupId: string | null,
  routesBySource: Map<string, TelegramCopyRoute[]>,
  groupsById: Map<string, TelegramGroup>,
): { destinationLabels: readonly string[] } {
  if (!sourceGroupId) {
    return { destinationLabels: ["sem destino configurado"] };
  }

  const routes = routesBySource.get(sourceGroupId) ?? [];
  if (routes.length === 0) {
    return { destinationLabels: ["sem destino configurado"] };
  }

  return {
    destinationLabels: routes.map((route, index) => {
      const destination = groupsById.get(route.destination_group_id);
      return `grupo ${index + 1}: ${groupTitle(destination, route.destination_group_id)}`;
    }),
  };
}

function groupTitle(group: TelegramGroup | undefined, fallback: string): string {
  if (!group) return fallback;
  return group.title ?? String(group.telegram_chat_id);
}

function isCredentialHealthy(status: string): boolean {
  return status === "valid" || status === "active";
}

function shortenUrl(value: string): string {
  return value.length > 44 ? `${value.slice(0, 44)}...` : value;
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const diff = Math.floor((now - new Date(iso).getTime()) / 1000);

  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}
